import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

/**
 * Hook personnalisé pour la gestion de la langue dans le Projet Albion
 * Fournit les fonctions de traduction et de changement de langue
 * 
 * @returns {Object} Objet contenant la fonction de traduction, la langue actuelle et la fonction de changement
 */
export const useLanguage = () => {
  const { t, i18n } = useTranslation();
  const logSentRef = useRef<string | null>(null); // Garder trace de la dernière langue pour laquelle un log a été envoyé

  // Sauvegarder la langue dans localStorage à chaque changement
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      localStorage.setItem('ALBION_LANGUAGE', lng);
      
      // Émettre un événement pour notifier les autres composants
      window.dispatchEvent(new CustomEvent('LANGUAGE_CHANGED', { 
        detail: { language: lng } 
      }));
      
      // Log système pour l'immersion - SEULEMENT si ce n'est pas déjà fait pour cette langue
      if (logSentRef.current !== lng) {
        logSentRef.current = lng;
        window.dispatchEvent(new CustomEvent('SCANNER_LOG', { 
          detail: { 
            message: lng === 'fr' 
              ? i18n.t('systemLogs.languageChanged')
              : i18n.t('systemLogs.languageChangedEn')
          } 
        }));
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  /**
   * Change la langue de l'application
   * @param {string} lng - Code de langue ('fr' ou 'en')
   */
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return {
    t, // Fonction de traduction
    language: i18n.language, // Langue actuelle
    changeLanguage, // Fonction pour changer la langue
    languages: ['fr', 'en'] // Langues disponibles
  };
};
